// Search-state for the Portal apps index route. AppRouter owns the
// validateSearch schema and stripSearchParams middleware; this hook is the
// typed surface over TanStack Router used by the page.

import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { DEFAULT_PAGE_SIZE } from '@components/DataTable/constants';

export const PORTAL_APP_TAB_VALUES = ['active', 'revoked'] as const;
export type PortalAppTab = (typeof PORTAL_APP_TAB_VALUES)[number];

export interface PortalAppsSearchParams {
  status: PortalAppTab;
  q: string;
  page: number;
  perPage: number;
  sort: string;
  direction: 'asc' | 'desc' | '';
}

export const PORTAL_APPS_SEARCH_DEFAULTS: PortalAppsSearchParams = {
  status: 'active',
  q: '',
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  sort: '',
  direction: '',
};

const PORTAL_APPS_ROUTE = '/portal_apps' as const;
// The API caps per_page at 200; a larger URL value would make the page count
// disagree with what the server returns.
export const PORTAL_APPS_MAX_PER_PAGE = 200;
const VALID_DIRECTIONS: ReadonlySet<'asc' | 'desc'> = new Set(['asc', 'desc']);

export function parsePortalAppsSearch(raw: Record<string, unknown>): PortalAppsSearchParams {
  const status = (PORTAL_APP_TAB_VALUES as readonly string[]).includes(String(raw.status))
    ? (raw.status as PortalAppTab)
    : PORTAL_APPS_SEARCH_DEFAULTS.status;
  const page = Number(raw.page);
  const perPage = Number(raw.perPage);
  const direction = VALID_DIRECTIONS.has(raw.direction as 'asc' | 'desc')
    ? (raw.direction as 'asc' | 'desc')
    : PORTAL_APPS_SEARCH_DEFAULTS.direction;

  return {
    status,
    q: typeof raw.q === 'string' ? raw.q : PORTAL_APPS_SEARCH_DEFAULTS.q,
    page: Number.isInteger(page) && page > 0 ? page : PORTAL_APPS_SEARCH_DEFAULTS.page,
    perPage:
      Number.isInteger(perPage) && perPage > 0
        ? Math.min(perPage, PORTAL_APPS_MAX_PER_PAGE)
        : PORTAL_APPS_SEARCH_DEFAULTS.perPage,
    sort: typeof raw.sort === 'string' ? raw.sort : PORTAL_APPS_SEARCH_DEFAULTS.sort,
    direction,
  };
}

export function usePortalAppsRouterSearch() {
  const search = useSearch({ from: PORTAL_APPS_ROUTE });
  const tanNavigate = useNavigate({ from: PORTAL_APPS_ROUTE });

  const navigate = useCallback(
    (updates: Partial<PortalAppsSearchParams>) => {
      tanNavigate({ search: (prev) => ({ ...prev, ...updates }), replace: true });
    },
    [tanNavigate],
  );

  return useMemo(
    () => ({
      search,
      navigate,
      setStatus: (status: PortalAppTab) => navigate({ status, page: 1 }),
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
      setSort: (sort: string, direction: 'asc' | 'desc' | '') => navigate({ sort, direction, page: 1 }),
    }),
    [search, navigate],
  );
}
